import GoogleMap from "@/app/activity/components/googleMap/GoogleMap"
import { createActivityStore } from "@/app/activity/store"
import Selector from "@/components/form/selector/Selector"
import { useStadiumInfoQuery, useStadiumListQuery } from "@/hooks/api/useStadiumQuery"
import { useActivityDataQuery } from "@/hooks/api/useActivityQuery"
import { Activity } from "@/types/activity"
import { Stadium } from "@/types/stadium"
import { debounce } from "lodash"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import "./mapWithComboBox.scss"
import RegisterStadiumDialog from "@/app/activity/components/registStadiumDialog/RegistStadiumDialog"

export default function MapWithComboBox() {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [searchValue, setSearchValue] = useState("")
  const [stadiumList, setStadiumList] = useState<Stadium[]>([])
  const [stadium, setStadium] = useState<Stadium | null>(null)

  const [center, setCenter] = useState<{ lat?: number; lng?: number }>({
    lat: 37.2864699567,
    lng: 127.036875349,
  })

  const latestSearchValueRef = useRef<string>("")
  const latestPageRef = useRef<number>(1)

  const { tempActivity, setTempActivity } = createActivityStore((state) => ({
    tempActivity: state.tempActivity,
    setTempActivity: state.setTempActivity,
  }))

  const { data: stadiumQueryRes } = useStadiumListQuery({
    name: searchValue,
    latitude: center.lat,
    longitude: center.lng,
    page,
  })

  const { data: stadiumInfo } = useStadiumInfoQuery(tempActivity?.idfStadium as number)
  const { data: activityData } = useActivityDataQuery(tempActivity?.idfActivity)

  // 액티비티 데이터의 평균 좌표 계산
  const activityAvgCenter = useMemo(() => {
    if (!activityData || activityData.length === 0) return null

    const validData = activityData.filter(
      (data) =>
        data.avgLatitude && data.avgLongitude && data.avgLatitude !== 0 && data.avgLongitude !== 0,
    )

    if (validData.length === 0) return null

    const avgLat = validData.reduce((sum, data) => sum + data.avgLatitude, 0) / validData.length
    const avgLng = validData.reduce((sum, data) => sum + data.avgLongitude, 0) / validData.length

    return { lat: avgLat, lng: avgLng }
  }, [activityData])

  const onSelectorMenuOpen = () => {
    // 현재 선택된 경기장이 있으면 그 이름으로 검색
    if (stadiumInfo?.name) {
      setSearchValue(stadiumInfo.name)
      latestSearchValueRef.current = stadiumInfo.name
    }
  }

  const debouncedSearch = debounce((e: React.KeyboardEvent<HTMLDivElement>) => {
    const newSearchValue = (e.target as HTMLInputElement).value

    // 최신 검색어와 페이지를 즉시 ref에 저장 (동기적)
    latestSearchValueRef.current = newSearchValue
    latestPageRef.current = 1

    // 검색어가 변경되면 상태 초기화
    setPage(1)
    setCenter({ lat: undefined, lng: undefined })

    // 빈 문자열이 아닐 때만 목록 초기화 (빈 문자열 응답이 늦게 와서 목록을 비우는 것 방지)
    if (newSearchValue.trim()) {
      setStadiumList([])
    }

    setSearchValue(newSearchValue)
  }, 800)

  // page가 변경될 때마다 ref 업데이트
  useEffect(() => {
    latestPageRef.current = page
  }, [page])

  useEffect(() => {
    if (!stadiumQueryRes) return

    // race condition 방지: API 응답이 최신 검색어와 페이지에 대한 것인지 확인
    // 응답이 도착했을 때 이미 새로운 검색어나 페이지가 요청되었다면 무시
    if (searchValue !== latestSearchValueRef.current || page !== latestPageRef.current) {
      return
    }

    // 대소문자 구분 없이 클라이언트 사이드 필터링
    const filteredContent = (stadiumQueryRes.content || []).filter((stadium) => {
      if (!searchValue.trim()) return true
      return stadium.name?.toLowerCase().includes(searchValue.toLowerCase())
    })

    // page가 1이면 새로 시작 (무조건 교체), 아니면 누적
    setStadiumList((prev) => (page === 1 ? filteredContent : [...prev, ...filteredContent]))
  }, [stadiumQueryRes])

  useEffect(() => {
    if (stadiumInfo) {
      // 1순위: 이미 선택된 경기장
      setStadium(stadiumInfo)
      setCenter({ lat: stadiumInfo.latitude, lng: stadiumInfo.longitude })
    } else if (activityAvgCenter) {
      // 2순위: 액티비티 데이터의 평균 좌표 (경기장이 없을 때)
      setStadium(null)
      setCenter(activityAvgCenter)
    }
    // 3순위: recommendStadium은 GoogleMap 내부에서 처리
    // 4순위: 기본 좌표는 초기 state로 이미 설정되어 있음
  }, [stadiumInfo, activityAvgCenter])

  return (
    <div className="MapWithComboBoxCont">
      <Selector
        value={stadiumInfo?.idfStadium ? stadiumInfo?.name + "-" + stadiumInfo?.idfStadium : ""}
        label={
          <>
            <span>
              {t("location")} <i>({t("optional")})</i>
            </span>
          </>
        }
        options={stadiumList}
        labelKey="name"
        onOpen={onSelectorMenuOpen}
        filterOptions={(options) => options}
        onKeyDown={debouncedSearch}
        placeholder={t("selectOrEnterLocation")}
        className="activity-selector-container"
        readOnly={false}
        onChange={(_: unknown, value: Stadium | null) => {
          setTempActivity({ ...tempActivity, idfStadium: value?.idfStadium } as Activity)
        }}
      />
      <div className="googleMap">
        <GoogleMap
          setCenter={setCenter}
          setSearchValue={setSearchValue}
          selectedStadium={stadium || undefined}
          center={center}
        />

        {!stadium && (
          <div className="no-stadium">
            <span>{t("selectOrEnterLocation")}</span>
          </div>
        )}
        <RegisterStadiumDialog />
      </div>
    </div>
  )
}
